import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { subscriptions as api, invoices as invoiceApi, archive } from '@/api/endpoints';
import { hasPermission } from '@/types';
import type {
  InvoiceCandidate, ManualInvoiceInput, Subscription, SubscriptionDetail, SubscriptionPeriod,
} from '@/types';
import { Icon } from '@/components/Icon';
import { useAuth } from '@/auth/AuthContext';
import { validateUploadSize, type UploadProgress } from '@/lib/uploadLimits';
import { UploadProgressBar } from '@/components/UploadProgress';
import { formatCurrency } from '@/lib/money';

const ROW_TONE: Record<string, string> = {
  OVERDUE: 'bg-red-50/40',
  DUE_SOON: 'bg-amber-50/30',
  OK: '',
  SETTLED: '',
};

/**
 * Szczegóły subskrypcji (V153): nagłówek + okresy rozliczeniowe.
 *
 * Tu dzieje się to, o co chodzi w module: dla każdego okresu widać czy jest faktura i czy
 * zapłacono. Fakturę można przypiąć z podpowiedzi KSeF albo dodać ręcznie razem ze skanem
 * (dostawcy zagraniczni nie wystawiają przez KSeF).
 */
export function SubskrypcjaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const subId = Number(id);
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = hasPermission(user, 'FINANCE_MANAGE');

  const [data, setData] = useState<SubscriptionDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [linking, setLinking] = useState<SubscriptionPeriod | null>(null);
  const [editingPeriod, setEditingPeriod] = useState<SubscriptionPeriod | null>(null);

  const reload = () => api.detail(subId).then(setData).catch(e => setErr(e?.message ?? String(e)));
  useEffect(() => { setData(null); setErr(null); reload(); /* eslint-disable-next-line */ }, [subId]);

  const act = (periodId: number, p: Promise<unknown>) => {
    setBusyId(periodId); setErr(null);
    p.then(() => { setBusyId(null); reload(); })
     .catch(e => { setBusyId(null); setErr(e?.message ?? String(e)); });
  };

  if (err && !data) return <div className="text-red-500 text-[13px]">Błąd: {err}</div>;
  if (!data) return <div className="text-ink-400">Ładowanie…</div>;

  const s = data.subscription;

  return (
    <div className="max-w-[1300px]">
      <header className="mb-5">
        <Link to="/subskrypcje" className="text-[12px] text-water-700 hover:underline">← Subskrypcje</Link>
        <div className="flex items-end justify-between flex-wrap gap-3 mt-1">
          <div>
            <h1>{s.name}</h1>
            <div className="text-ink-400 text-[13px] flex items-center gap-2 flex-wrap">
              <span className={'pill pill-' + s.categoryColor}>{s.categoryName}</span>
              <span className={'pill pill-' + s.statusColor}>{s.statusName}</span>
              <span>{s.vendorName}</span>
              {s.vendorForeign && (
                <span className="pill pill-ai" title="Dostawca zagraniczny — faktury nie ma w KSeF">zagraniczny</span>
              )}
              {s.referenceNumber && <span className="font-mono text-[11px]">nr {s.referenceNumber}</span>}
            </div>
          </div>
        </div>
      </header>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}

      <SubscriptionSummary s={s}/>

      <div className="card mt-4">
        <div className="card-head">
          <div className="card-title">Okresy rozliczeniowe</div>
          <span className="ml-2 text-[11px] text-ink-400">{data.periods.length}</span>
          {s.missingInvoices > 0 && (
            <span className="pill pill-amber ml-auto text-[11px]">
              brak faktury: {s.missingInvoices}
            </span>
          )}
        </div>
        <div className="card-body !p-0">
          {data.periods.length === 0 && (
            <div className="p-6 text-center text-ink-400 text-[12px]">
              Brak okresów. Powstaną automatycznie po zapisaniu daty początku i cyklu
              (portal generuje je jeden cykl naprzód, bez cofania się przed dzień wprowadzenia subskrypcji).
            </div>
          )}
          {data.periods.length > 0 && (
            <table className="table-hw">
              <thead>
                <tr>
                  <th>Okres</th>
                  <th>Termin</th>
                  <th className="text-right">Kwota</th>
                  <th>Faktura</th>
                  <th>Zapłata</th>
                  <th className="text-right">Akcje</th>
                </tr>
              </thead>
              <tbody>
                {data.periods.map(p => {
                  const busy = busyId === p.id;
                  const skipped = p.status === 'POMINIETY';
                  return (
                    <tr key={p.id} className={(ROW_TONE[p.urgency] ?? '') + (busy ? ' opacity-50' : '')}>
                      <td className="font-mono text-[12px] text-ink-700">
                        {p.periodFrom} – {p.periodTo}
                        {skipped && <span className="pill pill-gray text-[10px] ml-1">pominięty</span>}
                        {p.notes && <div className="text-[10px] text-ink-400 italic max-w-[220px] truncate" title={p.notes}>{p.notes}</div>}
                      </td>
                      <td className="font-mono text-[12px]">
                        <div className={p.urgency === 'OVERDUE' ? 'text-red-600 font-semibold' : 'text-ink-700'}>{p.dueDate}</div>
                        {!p.paid && !skipped && (
                          <div className={'text-[10px] ' + (p.urgency === 'OVERDUE' ? 'text-red-500' : 'text-ink-400')}>
                            {p.daysUntil === 0 ? 'dziś'
                              : p.daysUntil > 0 ? `za ${p.daysUntil} dni`
                              : `${-p.daysUntil} dni po terminie`}
                          </div>
                        )}
                      </td>
                      <td className="text-right font-mono text-[13px] text-ink-900">
                        {formatCurrency(p.plannedAmount, p.currency)}
                      </td>
                      <td className="text-[12px]">
                        {p.invoiceId ? (
                          <button onClick={() => navigate(`/faktury/${p.invoiceId}`)}
                                  className="text-left hover:underline text-water-700"
                                  title="Otwórz fakturę">
                            <div className="font-mono">{p.invoiceNumber ?? `#${p.invoiceId}`}</div>
                            {/* Waluta FAKTURY, nie okresu — przypiąć można dokument w innej walucie. */}
                            <div className="text-[11px] text-ink-500">{formatCurrency(p.invoiceGross, p.invoiceCurrency ?? p.currency)}</div>
                          </button>
                        ) : p.invoiceMissing ? (
                          <span className="pill pill-amber" title="Termin minął, a faktury nadal nie ma">brak faktury</span>
                        ) : (
                          <span className="text-ink-400">—</span>
                        )}
                      </td>
                      <td className="text-[12px]">
                        {p.paid ? (
                          <>
                            <span className="pill pill-green">Opłacone</span>
                            <div className="text-[10px] text-ink-400 mt-0.5">
                              {p.paidDate ?? ''}{p.paidSource === 'FAKTURA' ? ' · z faktury' : p.paidSource === 'RECZNIE' ? ' · ręcznie' : ''}
                            </div>
                          </>
                        ) : skipped ? (
                          <span className="text-ink-400">—</span>
                        ) : p.invoicePaymentStatus ? (
                          <span className={'pill pill-' + p.invoicePaymentStatusColor}>{p.invoicePaymentStatusName}</span>
                        ) : (
                          <span className="pill pill-amber">Do zapłaty</span>
                        )}
                      </td>
                      <td className="text-right whitespace-nowrap">
                        {canManage && (
                          <div className="flex items-center justify-end gap-1">
                            {!skipped && (
                              p.paid
                                ? <button onClick={() => act(p.id, api.unmarkPaid(p.id))} disabled={busy}
                                          className="btn btn-ghost btn-sm text-green-600" title="Cofnij oznaczenie zapłaty">
                                    <Icon name="check" className="w-3.5 h-3.5"/> Opłacone
                                  </button>
                                : <button onClick={() => act(p.id, api.markPaid(p.id))} disabled={busy}
                                          className="btn btn-secondary btn-sm"
                                          title={p.invoiceId ? 'Oznacz fakturę jako opłaconą' : 'Oznacz jako opłacone (bez faktury)'}>
                                    <Icon name="check" className="w-3.5 h-3.5"/> Opłać
                                  </button>
                            )}
                            {!skipped && (
                              p.invoiceId
                                ? <button onClick={() => act(p.id, api.detachInvoice(p.id))} disabled={busy}
                                          className="text-ink-400 hover:text-amber-700 px-1" title="Odepnij fakturę">
                                    <Icon name="x" className="w-3.5 h-3.5 inline"/>
                                  </button>
                                : <button onClick={() => setLinking(p)} disabled={busy}
                                          className="btn btn-secondary btn-sm" title="Znajdź lub dodaj fakturę">
                                    <Icon name="file-text" className="w-3.5 h-3.5"/> Faktura
                                  </button>
                            )}
                            <button onClick={() => setEditingPeriod(p)} disabled={busy}
                                    className="text-water-700 hover:text-water-900 px-1"
                                    title="Popraw termin lub kwotę okresu">
                              <Icon name="edit" className="w-3.5 h-3.5 inline"/>
                            </button>
                            <button onClick={() => act(p.id, api.skip(p.id, !skipped))} disabled={busy}
                                    className="text-ink-400 hover:text-ink-700 px-1"
                                    title={skipped ? 'Przywróć okres' : 'Pomiń ten okres (nienależny)'}>
                              <Icon name={skipped ? 'arrow' : 'more'} className="w-3.5 h-3.5 inline"/>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {linking && (
        <LinkInvoiceModal
          period={linking}
          subscription={s}
          onClose={() => setLinking(null)}
          onLinked={() => { setLinking(null); reload(); }}
        />
      )}

      {editingPeriod && (
        <PeriodEditModal
          period={editingPeriod}
          onClose={() => setEditingPeriod(null)}
          onSaved={() => { setEditingPeriod(null); reload(); }}
        />
      )}
    </div>
  );
}

/**
 * Korekta pojedynczego okresu. Generator ustawia termin na początek okresu (polisa płatna
 * z góry), ale podatek ma datę wyznaczoną ustawą, a faktura bywa z odroczeniem — bez tego okna
 * endpoint korekty istniał w API, a użytkownik nie miał jak go użyć.
 */
function PeriodEditModal({ period, onClose, onSaved }: {
  period: SubscriptionPeriod;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [dueDate, setDueDate] = useState(period.dueDate);
  const [amount, setAmount] = useState<string>(period.plannedAmount == null ? '' : String(period.plannedAmount));
  const [notes, setNotes] = useState(period.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = () => {
    setBusy(true); setErr(null);
    api.updatePeriod(period.id, {
      dueDate,
      plannedAmount: amount === '' ? null : Number(amount),
      notes: notes.trim() || null,
    }).then(onSaved).catch(e => { setBusy(false); setErr(e?.message ?? String(e)); });
  };

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: { dueDate, amount, notes }, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md my-6" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-ink-100 flex items-center">
          <div>
            <div className="font-semibold text-[14px]">Korekta okresu</div>
            <div className="text-[11px] text-ink-400">{period.periodFrom} – {period.periodTo}</div>
          </div>
          <button onClick={requestClose} className="ml-auto text-ink-400 hover:text-ink-700"><Icon name="x" className="w-4 h-4"/></button>
        </div>
        <div className="p-5 grid gap-3">
          {err && <div className="text-red-600 bg-red-50 border border-red-200 rounded p-2 text-[12px]">{err}</div>}
          <label className="field"><span className="field-label">Termin płatności</span>
            <input type="date" className="input" value={dueDate} onChange={e => setDueDate(e.target.value)}/></label>
          <div className="field-hint -mt-1">
            Zmiana dotyczy tylko tego okresu — nie przesuwa kolejnych ani kotwicy subskrypcji.
          </div>
          <label className="field"><span className="field-label">Kwota za okres</span>
            <input type="number" step="0.01" min="0" className="input" value={amount}
                   onChange={e => setAmount(e.target.value)} placeholder="puste = bez kwoty"/></label>
          <label className="field"><span className="field-label">Notatka</span>
            <textarea className="input" rows={2} value={notes} onChange={e => setNotes(e.target.value)}/></label>
        </div>
        <div className="px-5 py-3 border-t border-ink-100 flex justify-end gap-2">
          <button onClick={requestClose} className="btn btn-secondary btn-sm">Anuluj</button>
          <button onClick={save} disabled={busy || !dueDate} className="btn btn-primary btn-sm">
            <Icon name="check" className="w-3.5 h-3.5"/> {busy ? 'Zapisywanie…' : 'Zapisz'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SubscriptionSummary({ s }: { s: Subscription }) {
  const rows: [string, React.ReactNode][] = [
    ['Dostawca', <>
      {s.vendorName ?? '—'}
      {s.vendorForeign && <span className="text-ink-400"> · faktura poza KSeF</span>}
    </>],
    ['Cykl', s.billingCycleName],
    ['Kwota za okres', formatCurrency(s.plannedAmount, s.currency)],
    ['Koszt roczny', formatCurrency(s.yearlyAmount, s.currency)],
    ['Obowiązuje', `${s.startDate}${s.endDate ? ` – ${s.endDate}` : ' – bezterminowo'}`],
    ['Następny termin', s.nextDueDate ?? '—'],
    ['Sposób zapłaty', s.paymentMethodName + (s.paymentAutomatic ? ' (schodzi automatycznie)' : '')],
    ['Przypomnienie', `${s.remindDaysBefore ?? 30} dni przed terminem`],
    ['Faktura', s.invoiceExpected ? 'oczekiwana' : 'nieoczekiwana (podatek/składka)'],
    ['Odnowienie', s.autoRenew ? 'automatyczne' : 'wymaga działania'],
    ['Płatnik', s.ourCompanyName ?? '—'],
  ];
  return (
    <div className="card">
      <div className="card-head"><div className="card-title">Dane subskrypcji</div></div>
      <div className="card-body grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-[12px]">
        {rows.map(([k, v]) => (
          <div key={k}>
            <div className="text-ink-400 text-[10px] uppercase tracking-wide">{k}</div>
            <div className="text-ink-800">{v}</div>
          </div>
        ))}
        {s.description && (
          <div className="col-span-2 md:col-span-3">
            <div className="text-ink-400 text-[10px] uppercase tracking-wide">Opis</div>
            <div className="text-ink-800 whitespace-pre-wrap">{s.description}</div>
          </div>
        )}
        {s.notes && (
          <div className="col-span-2 md:col-span-3">
            <div className="text-ink-400 text-[10px] uppercase tracking-wide">Notatki</div>
            <div className="text-ink-800 whitespace-pre-wrap">{s.notes}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Przypięcie faktury: podpowiedzi z KSeF albo ręczne dodanie ze skanem
// ═══════════════════════════════════════════════════════════════════════════

function LinkInvoiceModal({ period, subscription, onClose, onLinked }: {
  period: SubscriptionPeriod;
  subscription: Subscription;
  onClose: () => void;
  onLinked: () => void;
}) {
  const [candidates, setCandidates] = useState<InvoiceCandidate[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState(false);

  useEffect(() => {
    api.invoiceCandidates(period.id).then(setCandidates).catch(e => { setCandidates([]); setErr(String(e)); });
  }, [period.id]);

  const attach = (invoiceId: number) => {
    setBusy(true); setErr(null);
    api.attachInvoice(period.id, invoiceId)
      .then(onLinked)
      .catch(e => { setBusy(false); setErr(e?.message ?? String(e)); });
  };

  if (manual) {
    return (
      <ManualInvoiceModal
        period={period}
        subscription={subscription}
        onClose={() => setManual(false)}
        onDone={onLinked}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      {/* max-w-4xl, nie 3xl: przy 3xl tabela kandydatów (800 px) chowała przycisk „Przypnij" za krawędzią. */}
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl my-6" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-ink-100 flex items-center">
          <div>
            <div className="font-semibold text-[14px]">Faktura za okres {period.periodFrom} – {period.periodTo}</div>
            <div className="text-[11px] text-ink-400">
              {subscription.name} · {subscription.vendorName} · plan {formatCurrency(period.plannedAmount, period.currency)}
            </div>
          </div>
          <button onClick={onClose} className="ml-auto text-ink-400 hover:text-ink-700"><Icon name="x" className="w-4 h-4"/></button>
        </div>

        <div className="p-0">
          {err && <div className="p-3 text-red-700 bg-red-50 border-b border-red-200 text-[12px]">{err}</div>}

          <div className="px-5 py-3 border-b border-ink-100 flex items-center gap-2 bg-ink-50/50">
            <div className="text-[12px] text-ink-600">
              {subscription.vendorForeign
                ? 'Dostawca zagraniczny — faktury nie ma w KSeF. Pobierz PDF z panelu dostawcy i dodaj ręcznie.'
                : 'Poniżej faktury z KSeF pasujące do tego okresu. Nic nie zostanie przypięte bez Twojego kliknięcia.'}
            </div>
            <button onClick={() => setManual(true)} className="ml-auto btn btn-primary btn-sm whitespace-nowrap">
              <Icon name="upload" className="w-3.5 h-3.5"/> Dodaj fakturę ręcznie
            </button>
          </div>

          {candidates == null && <div className="p-4 text-ink-400 text-[12px]">Szukam pasujących faktur…</div>}
          {candidates && candidates.length === 0 && (
            <div className="p-6 text-center text-ink-400 text-[12px]">
              <Icon name="search" className="w-8 h-8 mx-auto mb-2 text-ink-300"/>
              Brak pasujących faktur w KSeF dla tego okresu.<br/>
              Użyj <strong>Dodaj fakturę ręcznie</strong>, jeśli masz dokument w PDF.
            </div>
          )}
          {candidates && candidates.length > 0 && (
            // overflow-x-auto: bez tego tabela (830 px) wylewa się poza ramkę modala (768 px).
            <div className="overflow-x-auto">
              <table className="table-hw">
                <thead>
                  <tr>
                    <th>Numer</th><th>Sprzedawca</th><th>Wystawiona</th>
                    <th className="text-right">Brutto</th><th>Dopasowanie</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map(c => (
                    <tr key={c.invoice.id}>
                      <td className="font-mono text-[12px]">{c.invoice.invoiceNumber ?? '—'}</td>
                      <td className="text-[12px]">
                        <div className="truncate max-w-[170px]" title={c.invoice.sellerName ?? undefined}>
                          {c.invoice.sellerName ?? '—'}
                        </div>
                        <div className="font-mono text-[10px] text-ink-400">{c.invoice.sellerNip ?? ''}</div>
                      </td>
                      <td className="font-mono text-[12px] text-ink-500 whitespace-nowrap">{c.invoice.issueDate ?? '—'}</td>
                      <td className="text-right font-mono text-[13px] whitespace-nowrap">{formatCurrency(c.invoice.totalGross, c.invoice.currency)}</td>
                      <td className="text-[11px]">
                        <span className={'pill ' + (c.score >= 70 ? 'pill-green' : c.score >= 45 ? 'pill-amber' : 'pill-gray')}>
                          {c.score}%
                        </span>
                        <div className="text-ink-400 mt-0.5 max-w-[150px]" title={c.reason}>{c.reason}</div>
                      </td>
                      <td className="text-right">
                        <button onClick={() => attach(c.invoice.id)} disabled={busy} className="btn btn-secondary btn-sm whitespace-nowrap">
                          <Icon name="check" className="w-3.5 h-3.5"/> Przypnij
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Ręczne dodanie faktury spoza KSeF razem ze skanem. Trzy kroki w jednym oknie, bo w praktyce
 * to jedna czynność: utwórz nagłówek → wgraj PDF → przypnij do okresu. Gdy upload padnie,
 * faktura i tak zostaje przypięta — plik można dołożyć później na ekranie faktury.
 */
function ManualInvoiceModal({ period, subscription, onClose, onDone }: {
  period: SubscriptionPeriod;
  subscription: Subscription;
  onClose: () => void;
  onDone: () => void;
}) {
  const [form, setForm] = useState<ManualInvoiceInput>({
    direction: 'PURCHASE',
    invoiceNumber: '',
    issueDate: period.periodFrom,
    saleDate: null,
    dueDate: period.dueDate,
    sellerNip: null,
    sellerName: subscription.vendorName ?? '',
    buyerNip: null,
    buyerName: subscription.ourCompanyName ?? null,
    ourCompanyId: subscription.ourCompanyId ?? null,
    counterpartyClientId: null,
    currency: period.currency,
    totalNet: null,
    totalVat: null,
    totalGross: period.plannedAmount ?? 0,
    notes: `${subscription.name} · okres ${period.periodFrom} – ${period.periodTo}`,
  });
  const [file, setFile] = useState<File | null>(null);
  const [pct, setPct] = useState<number | null>(null);
  const [step, setStep] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * Id faktury utworzonej w tym oknie. Bez tego ponowienie po błędzie przypięcia zaczynało od
   * nowa od createManual i zakładało DRUGĄ fakturę o tym samym numerze — backend nie ma dedupu
   * po numerze (UNIQUE jest tylko na ksef_number).
   */
  const [utworzonaFaktura, setUtworzonaFaktura] = useState<number | null>(null);

  const set = <K extends keyof ManualInvoiceInput>(k: K, v: ManualInvoiceInput[K]) =>
    setForm(f => ({ ...f, [k]: v }));

  const pickFile = (list: FileList | null) => {
    const f = list?.[0] ?? null;
    if (!f) { setFile(null); return; }
    const sizeErr = validateUploadSize([f]);
    if (sizeErr) { setErr(sizeErr); setFile(null); return; }
    setErr(null);
    setFile(f);
  };

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      let invoiceId = utworzonaFaktura;
      if (invoiceId == null) {
        setStep('Zapisywanie faktury…');
        const invoice = await invoiceApi.createManual(form);
        invoiceId = invoice.id;
        setUtworzonaFaktura(invoiceId);
      }
      const invoice = { id: invoiceId };

      if (file) {
        setStep('Wysyłanie skanu…');
        setPct(0);
        try {
          await archive.uploadForInvoice(invoice.id, file, { ragEnabled: false },
            (p: UploadProgress) => setPct(p.percent));
          setFile(null);   // wgrany — ponowienie po błędzie przypięcia nie wyśle go drugi raz
        } catch (e) {
          // Skan to dodatek — nagłówek faktury już istnieje i musi trafić do okresu,
          // inaczej user zostaje z fakturą-sierotą i bez powiązania.
          setErr(`Faktura zapisana, ale skan się nie wgrał: ${e instanceof Error ? e.message : String(e)}`);
        } finally {
          setPct(null);
        }
      }

      setStep('Przypinanie do okresu…');
      await api.attachInvoice(period.id, invoice.id);
      onDone();
    } catch (e) {
      setBusy(false); setStep(null); setPct(null);
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: { form, file }, onClose, busy,
  });

  return (
    // stopPropagation na overlayu — patrz VendorFormModal: bez tego kliknięcie w tło zamykałoby
    // także okno nadrzędne razem z wypełnionym formularzem faktury.
    <div className="fixed inset-0 z-[55] bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
         onClick={e => { e.stopPropagation(); requestClose(); }} {...dialogProps}>
      {confirmation}
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl my-6" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-ink-100 flex items-center">
          <div>
            <div className="font-semibold text-[14px]">Faktura spoza KSeF</div>
            <div className="text-[11px] text-ink-400">
              {subscription.vendorName} · okres {period.periodFrom} – {period.periodTo}
            </div>
          </div>
          <button onClick={requestClose} className="ml-auto text-ink-400 hover:text-ink-700"><Icon name="x" className="w-4 h-4"/></button>
        </div>

        <div className="p-5 grid gap-3">
          {err && <div className="text-red-600 bg-red-50 border border-red-200 rounded p-2 text-[12px]">{err}</div>}

          <div className="grid grid-cols-2 gap-3">
            <label className="field"><span className="field-label">Numer faktury *</span>
              <input className="input" value={form.invoiceNumber}
                     onChange={e => set('invoiceNumber', e.target.value)}
                     placeholder="np. INV-2026-00412"/></label>
            <label className="field"><span className="field-label">Sprzedawca</span>
              <input className="input" value={form.sellerName ?? ''} onChange={e => set('sellerName', e.target.value)}/></label>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <label className="field"><span className="field-label">Wystawiona</span>
              <input type="date" className="input" value={form.issueDate ?? ''}
                     onChange={e => set('issueDate', e.target.value || null)}/></label>
            <label className="field"><span className="field-label">Termin płatności</span>
              <input type="date" className="input" value={form.dueDate ?? ''}
                     onChange={e => set('dueDate', e.target.value || null)}/></label>
            <label className="field"><span className="field-label">NIP / VAT sprzedawcy</span>
              <input className="input" value={form.sellerNip ?? ''} onChange={e => set('sellerNip', e.target.value)}/></label>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <label className="field"><span className="field-label">Netto</span>
              <input type="number" step="0.01" className="input" value={form.totalNet ?? ''}
                     onChange={e => set('totalNet', e.target.value === '' ? null : Number(e.target.value))}/></label>
            <label className="field"><span className="field-label">VAT</span>
              <input type="number" step="0.01" className="input" value={form.totalVat ?? ''}
                     onChange={e => set('totalVat', e.target.value === '' ? null : Number(e.target.value))}/></label>
            <label className="field"><span className="field-label">Brutto *</span>
              <input type="number" step="0.01" min="0" className="input" value={form.totalGross}
                     onChange={e => set('totalGross', Number(e.target.value))}/></label>
          </div>

          <label className="field"><span className="field-label">Waluta</span>
            <input className="input uppercase w-24" maxLength={3} value={form.currency}
                   onChange={e => set('currency', e.target.value.toUpperCase())}/></label>

          <div className="field">
            <span className="field-label">Skan faktury (PDF / obraz)</span>
            <input type="file" className="text-[12px]" accept=".pdf,.png,.jpg,.jpeg,.webp"
                   onChange={e => pickFile(e.target.files)}/>
            {file && <div className="field-hint">{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</div>}
            {pct !== null && <div className="mt-1"><UploadProgressBar percent={pct} label="Wysyłanie skanu…" compact/></div>}
          </div>

          <label className="field"><span className="field-label">Notatki</span>
            <textarea className="input" rows={2} value={form.notes ?? ''} onChange={e => set('notes', e.target.value)}/></label>
        </div>

        <div className="px-5 py-3 border-t border-ink-100 flex items-center gap-2">
          {step && <span className="text-[12px] text-ink-500">{step}</span>}
          <div className="ml-auto flex gap-2">
            <button onClick={requestClose} disabled={busy} className="btn btn-secondary btn-sm">Anuluj</button>
            <button onClick={save} disabled={busy || !form.invoiceNumber.trim()} className="btn btn-primary btn-sm">
              <Icon name="check" className="w-3.5 h-3.5"/>
              {busy ? 'Zapisywanie…' : utworzonaFaktura != null ? 'Ponów przypięcie' : 'Zapisz i przypnij'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
